import { z } from "zod";

import { DEFAULT_YAK_TRAILS_CONFIG } from "./config";
import type {
  TournamentAdvancementPolicy,
  TournamentDefinition,
  TournamentEntrant,
  TournamentHeat,
  TournamentScoringPolicy,
  TournamentSeedPolicy,
  TournamentStage,
  TournamentStanding,
  TournamentValidationResult,
} from "./domain";
import { SeededRandom } from "./rng";

export interface TournamentValidationLimits {
  maximumStages: number;
  maximumEntrants: number;
  maximumRoomCapacity: number;
  maximumBotsPerRoom: number;
  maximumSinglePartitionEntrants?: number;
}

export const DEFAULT_TOURNAMENT_VALIDATION_LIMITS: TournamentValidationLimits = Object.freeze({
  maximumStages: DEFAULT_YAK_TRAILS_CONFIG.tournament.maximumStages,
  maximumEntrants: DEFAULT_YAK_TRAILS_CONFIG.tournament.maximumEntrants,
  maximumRoomCapacity: DEFAULT_YAK_TRAILS_CONFIG.world.capacity,
  maximumBotsPerRoom: DEFAULT_YAK_TRAILS_CONFIG.world.capacity - 1,
  maximumSinglePartitionEntrants: DEFAULT_YAK_TRAILS_CONFIG.tournament.entrantsPerPartition,
});

const seedPolicySchema: z.ZodType<TournamentSeedPolicy> = z.discriminatedUnion("type", [
  z.object({ type: z.literal("explicit") }).strict(),
  z.object({ type: z.literal("random"), seed: z.string().min(1) }).strict(),
  z.object({ type: z.literal("school_rank") }).strict(),
  z.object({ type: z.literal("prior_stage"), stageId: z.string().min(1) }).strict(),
]);

const scoringPolicySchema: z.ZodType<TournamentScoringPolicy> = z.discriminatedUnion("type", [
  z.object({ type: z.literal("peak_territory") }).strict(),
  z.object({ type: z.literal("final_territory") }).strict(),
  z.object({ type: z.literal("survival") }).strict(),
  z.object({ type: z.literal("placement_points"), points: z.array(z.number().finite().nonnegative()).min(1) }).strict(),
  z.object({ type: z.literal("team_total") }).strict(),
]);

const advancementPolicySchema: z.ZodType<TournamentAdvancementPolicy> = z.discriminatedUnion("type", [
  z.object({ type: z.literal("winner") }).strict(),
  z.object({ type: z.literal("top_k"), count: z.number().int().positive() }).strict(),
  z.object({ type: z.literal("points"), count: z.number().int().positive() }).strict(),
  z.object({ type: z.literal("team_top_k"), count: z.number().int().positive() }).strict(),
  z.object({ type: z.literal("staff_override") }).strict(),
]);

export const tournamentStageSchema: z.ZodType<TournamentStage> = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  order: z.number().int().nonnegative(),
  mode: z.enum(["ffa", "team"]),
  scope: z.enum(["school", "everyone"]),
  allowedSchoolIds: z.array(z.string().min(1)).optional(),
  teamStrategy: z.enum(["balanced", "school_vs_school", "within_school"]),
  rulesetVersion: z.string().min(1),
  configVersion: z.string().min(1),
  roomCapacity: z.number().int().min(2).max(128),
  botCount: z.number().int().nonnegative().max(128).optional(),
  checkInOpensAtMs: z.number().int().nonnegative(),
  startsAtMs: z.number().int().nonnegative(),
  durationMs: z.number().int().positive(),
  lateJoinWindowMs: z.number().int().nonnegative(),
  respawnPolicy: z.enum(["immediate", "delayed", "elimination"]),
  respawnDelayMs: z.number().int().nonnegative().optional(),
  scoring: scoringPolicySchema,
  seeding: seedPolicySchema,
  advancement: advancementPolicySchema,
  tieBreakers: z.array(z.enum(["peak_territory", "survival_time", "eliminations", "seed"])),
  nextStageId: z.string().min(1).optional(),
}).strict();

export const tournamentDefinitionSchema: z.ZodType<TournamentDefinition> = z.object({
  id: z.string().min(1),
  version: z.number().int().positive(),
  name: z.string().min(1),
  status: z.enum(["draft", "published", "paused", "active", "cancelled", "completed"]),
  createdAtMs: z.number().int().nonnegative(),
  updatedAtMs: z.number().int().nonnegative(),
  registrationOpensAtMs: z.number().int().nonnegative(),
  registrationClosesAtMs: z.number().int().nonnegative(),
  maxEntrants: z.number().int().positive().optional(),
  stages: z.array(tournamentStageSchema).min(1).max(64),
}).strict();

export function validateTournamentDefinition(
  input: unknown,
  limits: TournamentValidationLimits = DEFAULT_TOURNAMENT_VALIDATION_LIMITS,
): TournamentValidationResult {
  const parsed = tournamentDefinitionSchema.safeParse(input);
  if (!parsed.success) {
    return {
      valid: false,
      errors: parsed.error.issues.map((issue) => ({ path: issue.path.join("."), message: issue.message })),
    };
  }
  const definition = parsed.data;
  const errors: TournamentValidationResult["errors"] = [];
  if (definition.stages.length > limits.maximumStages) {
    errors.push({ path: "stages", message: `Tournament exceeds the configured ${limits.maximumStages}-stage maximum` });
  }
  if ((definition.maxEntrants ?? 0) > limits.maximumEntrants) {
    errors.push({ path: "maxEntrants", message: `Tournament exceeds the configured ${limits.maximumEntrants}-entrant maximum` });
  }
  if (definition.registrationClosesAtMs <= definition.registrationOpensAtMs) {
    errors.push({ path: "registrationClosesAtMs", message: "Registration must close after it opens" });
  }
  if (definition.updatedAtMs < definition.createdAtMs) {
    errors.push({ path: "updatedAtMs", message: "updatedAtMs cannot precede createdAtMs" });
  }

  const byId = new Map<string, TournamentStage>();
  const orders = new Set<number>();
  for (const [index, stage] of definition.stages.entries()) {
    if (byId.has(stage.id)) errors.push({ path: `stages.${index}.id`, message: `Duplicate stage ID: ${stage.id}` });
    byId.set(stage.id, stage);
    if (orders.has(stage.order)) errors.push({ path: `stages.${index}.order`, message: `Duplicate stage order: ${stage.order}` });
    orders.add(stage.order);
    if (stage.startsAtMs <= stage.checkInOpensAtMs) {
      errors.push({ path: `stages.${index}.startsAtMs`, message: "Stage must start after check-in opens" });
    }
    if (stage.roomCapacity > limits.maximumRoomCapacity) {
      errors.push({ path: `stages.${index}.roomCapacity`, message: `Room capacity exceeds the configured maximum of ${limits.maximumRoomCapacity}` });
    }
    if ((stage.botCount ?? 0) > Math.min(stage.roomCapacity - 1, limits.maximumBotsPerRoom)) {
      errors.push({ path: `stages.${index}.botCount`, message: `Bot count cannot exceed room capacity minus one or the configured maximum of ${limits.maximumBotsPerRoom}` });
    }
    if (stage.lateJoinWindowMs > stage.durationMs) {
      errors.push({ path: `stages.${index}.lateJoinWindowMs`, message: "Late-join window cannot exceed stage duration" });
    }
    if (stage.scope === "school" && stage.allowedSchoolIds?.length === 0) {
      errors.push({ path: `stages.${index}.allowedSchoolIds`, message: "School-scoped stages need at least one allowed school" });
    }
    if (stage.mode === "ffa" && stage.advancement.type === "team_top_k") {
      errors.push({ path: `stages.${index}.advancement`, message: "FFA stages cannot use team advancement" });
    }
    if (stage.mode === "ffa" && stage.teamStrategy !== "balanced") {
      errors.push({ path: `stages.${index}.teamStrategy`, message: "FFA stages must use balanced team strategy" });
    }
    if (stage.mode === "team" && stage.teamStrategy === "school_vs_school") {
      if (stage.scope !== "school") {
        errors.push({ path: `stages.${index}.scope`, message: "School-vs-school stages must use school scope" });
      }
      if (stage.allowedSchoolIds?.length !== 2) {
        errors.push({ path: `stages.${index}.allowedSchoolIds`, message: "School-vs-school stages require exactly two allowed schools" });
      }
      if ((definition.maxEntrants ?? 0) > (limits.maximumSinglePartitionEntrants ?? DEFAULT_YAK_TRAILS_CONFIG.tournament.entrantsPerPartition)) {
        errors.push({
          path: "maxEntrants",
          message: `School-vs-school stages are capped at ${limits.maximumSinglePartitionEntrants ?? DEFAULT_YAK_TRAILS_CONFIG.tournament.entrantsPerPartition} entrants so both verified schools allocate together`,
        });
      }
    }
    if (stage.mode === "team" && stage.teamStrategy === "within_school") {
      if (stage.scope !== "school") {
        errors.push({ path: `stages.${index}.scope`, message: "Within-school stages must use school scope" });
      }
      if (stage.allowedSchoolIds?.length !== 1) {
        errors.push({ path: `stages.${index}.allowedSchoolIds`, message: "Within-school stages require exactly one allowed school" });
      }
    }
    if (stage.respawnPolicy === "immediate" && (stage.respawnDelayMs ?? 0) !== 0) {
      errors.push({ path: `stages.${index}.respawnDelayMs`, message: "Immediate respawn stages must use a zero delay" });
    }
    if (stage.respawnPolicy === "delayed" && (stage.respawnDelayMs ?? 0) <= 0) {
      errors.push({ path: `stages.${index}.respawnDelayMs`, message: "Delayed respawn stages require a positive delay" });
    }
    if (stage.respawnPolicy === "elimination" && (stage.respawnDelayMs ?? 0) !== 0) {
      errors.push({ path: `stages.${index}.respawnDelayMs`, message: "Elimination stages cannot configure a respawn delay" });
    }
    if ((stage.advancement.type === "top_k" || stage.advancement.type === "points") && stage.advancement.count > stage.roomCapacity) {
      errors.push({ path: `stages.${index}.advancement.count`, message: "Advancement count cannot exceed room capacity" });
    }
    if (stage.advancement.type === "team_top_k" && stage.advancement.count > 2) {
      errors.push({ path: `stages.${index}.advancement.count`, message: "Two-team stages cannot advance more than two teams" });
    }
    if (new Set(stage.allowedSchoolIds ?? []).size !== (stage.allowedSchoolIds?.length ?? 0)) {
      errors.push({ path: `stages.${index}.allowedSchoolIds`, message: "Allowed school IDs must be unique" });
    }
    if (new Set(stage.tieBreakers).size !== stage.tieBreakers.length) {
      errors.push({ path: `stages.${index}.tieBreakers`, message: "Tie breakers must be unique" });
    }
  }

  for (const [index, stage] of definition.stages.entries()) {
    if (stage.nextStageId && !byId.has(stage.nextStageId)) {
      errors.push({ path: `stages.${index}.nextStageId`, message: `Unknown next stage: ${stage.nextStageId}` });
    }
    if (stage.nextStageId && (byId.get(stage.nextStageId)?.order ?? -1) <= stage.order) {
      errors.push({ path: `stages.${index}.nextStageId`, message: "A stage may only advance to a later stage" });
    }
    if (stage.seeding.type === "prior_stage" && !byId.has(stage.seeding.stageId)) {
      errors.push({ path: `stages.${index}.seeding.stageId`, message: `Unknown prior stage: ${stage.seeding.stageId}` });
    }
    if (stage.seeding.type === "prior_stage" && (byId.get(stage.seeding.stageId)?.order ?? Number.MAX_SAFE_INTEGER) >= stage.order) {
      errors.push({ path: `stages.${index}.seeding.stageId`, message: "Prior-stage seeding must reference an earlier stage" });
    }
  }
  const visitState = new Map<string, "visiting" | "visited">();
  const visit = (stageId: string): void => {
    if (visitState.get(stageId) === "visiting") {
      errors.push({ path: "stages", message: `Stage graph contains a cycle at ${stageId}` });
      return;
    }
    if (visitState.get(stageId) === "visited") return;
    visitState.set(stageId, "visiting");
    const next = byId.get(stageId)?.nextStageId;
    if (next) visit(next);
    visitState.set(stageId, "visited");
  };
  for (const stage of definition.stages) visit(stage.id);
  return { valid: errors.length === 0, errors };
}

export class TournamentDefinitionError extends Error {
  constructor(readonly validationErrors: TournamentValidationResult["errors"]) {
    super(validationErrors.map((error) => `${error.path}: ${error.message}`).join("; "));
    this.name = "TournamentDefinitionError";
  }
}

export function parseTournamentDefinition(
  input: unknown,
  limits: TournamentValidationLimits = DEFAULT_TOURNAMENT_VALIDATION_LIMITS,
): TournamentDefinition {
  const parsed = tournamentDefinitionSchema.parse(input);
  const validation = validateTournamentDefinition(parsed, limits);
  if (!validation.valid) throw new TournamentDefinitionError(validation.errors);
  return parsed;
}

export function buildStageGraph(definition: TournamentDefinition): {
  roots: TournamentStage[];
  byId: ReadonlyMap<string, TournamentStage>;
  incoming: ReadonlyMap<string, string[]>;
} {
  const validation = validateTournamentDefinition(definition);
  if (!validation.valid) throw new TournamentDefinitionError(validation.errors);
  const byId = new Map(definition.stages.map((stage) => [stage.id, stage]));
  const incoming = new Map<string, string[]>();
  for (const stage of definition.stages) incoming.set(stage.id, []);
  for (const stage of definition.stages) if (stage.nextStageId) incoming.get(stage.nextStageId)?.push(stage.id);
  const roots = definition.stages
    .filter((stage) => (incoming.get(stage.id)?.length ?? 0) === 0)
    .sort((left, right) => left.order - right.order || left.id.localeCompare(right.id));
  return { roots, byId, incoming };
}

export function seedEntrants(entrants: readonly TournamentEntrant[], policy: TournamentSeedPolicy): TournamentEntrant[] {
  const unique = new Map<string, TournamentEntrant>();
  for (const entrant of entrants) {
    if (unique.has(entrant.id)) throw new Error(`Duplicate entrant ID: ${entrant.id}`);
    unique.set(entrant.id, { ...entrant });
  }
  const values = [...unique.values()];
  if (policy.type === "explicit") {
    return values.sort((left, right) => (left.seed ?? Number.MAX_SAFE_INTEGER) - (right.seed ?? Number.MAX_SAFE_INTEGER) || left.id.localeCompare(right.id));
  }
  if (policy.type === "random") {
    return new SeededRandom(policy.seed).shuffle(values).map((entrant, index) => ({ ...entrant, seed: index + 1 }));
  }
  if (policy.type === "school_rank") {
    return values.sort((left, right) =>
      (left.schoolId ?? "~").localeCompare(right.schoolId ?? "~")
      || (left.seed ?? Number.MAX_SAFE_INTEGER) - (right.seed ?? Number.MAX_SAFE_INTEGER)
      || left.id.localeCompare(right.id)
    ).map((entrant, index) => ({ ...entrant, seed: index + 1 }));
  }
  return values.sort((left, right) =>
    (right.score ?? Number.NEGATIVE_INFINITY) - (left.score ?? Number.NEGATIVE_INFINITY)
    || (left.placement ?? Number.MAX_SAFE_INTEGER) - (right.placement ?? Number.MAX_SAFE_INTEGER)
    || left.id.localeCompare(right.id)
  ).map((entrant, index) => ({ ...entrant, seed: index + 1 }));
}

export function buildStageHeats(stage: TournamentStage, entrants: readonly TournamentEntrant[]): TournamentHeat[] {
  const seeded = seedEntrants(entrants, stage.seeding);
  if (seeded.length === 0) return [];
  const schoolTeams = stage.mode === "team" && stage.teamStrategy === "school_vs_school"
    ? new Map((stage.allowedSchoolIds ?? []).map((schoolId, index) => [schoolId, index === 0 ? "red" as const : "blue" as const]))
    : null;
  const assigned = seeded.map((entrant, index) => ({
    ...entrant,
    ...(stage.mode === "team"
      ? {
          teamId: schoolTeams
            ? schoolTeams.get(entrant.schoolId ?? "")
            : entrant.teamId ?? (index % 2 === 0 ? "red" as const : "blue" as const),
        }
      : {}),
  }));
  if (schoolTeams && assigned.some((entrant) => !entrant.teamId)) {
    throw new Error("School-vs-school heat contains an entrant from an ineligible school");
  }
  const redEntrants = assigned.filter((entrant) => entrant.teamId === "red");
  const blueEntrants = assigned.filter((entrant) => entrant.teamId === "blue");
  const redCapacity = Math.ceil(stage.roomCapacity / 2);
  const blueCapacity = Math.floor(stage.roomCapacity / 2);
  const heatCount = schoolTeams
    ? Math.max(
        1,
        Math.ceil(assigned.length / stage.roomCapacity),
        Math.ceil(redEntrants.length / redCapacity),
        Math.ceil(blueEntrants.length / Math.max(1, blueCapacity)),
      )
    : Math.ceil(assigned.length / stage.roomCapacity);
  const buckets = Array.from({ length: heatCount }, () => [] as TournamentEntrant[]);
  const distribute = (values: readonly TournamentEntrant[]) => {
    for (let index = 0; index < values.length; index += 1) {
      const round = Math.floor(index / heatCount);
      const position = index % heatCount;
      const heatIndex = round % 2 === 0 ? position : heatCount - 1 - position;
      buckets[heatIndex]?.push(values[index]!);
    }
  };
  if (schoolTeams) {
    distribute(redEntrants);
    distribute(blueEntrants);
  } else {
    distribute(assigned);
  }
  return buckets.map((heatEntrants, index) => {
    const hasRedTeam = heatEntrants.some((entrant) => entrant.teamId === "red");
    const hasBlueTeam = heatEntrants.some((entrant) => entrant.teamId === "blue");
    const isTeamBye = schoolTeams !== null && hasRedTeam !== hasBlueTeam;
    const isBye = heatEntrants.length === 1 || isTeamBye;
    return {
      id: `${stage.id}-heat-${index + 1}`,
      stageId: stage.id,
      index,
      entrants: isBye ? [] : heatEntrants,
      byeEntrants: isBye ? heatEntrants : [],
    };
  });
}

export function scoreStanding(
  policy: TournamentScoringPolicy,
  standing: Omit<TournamentStanding, "score" | "placement">,
): number {
  if (policy.type === "peak_territory") return standing.peakTerritoryPercent;
  if (policy.type === "final_territory") return standing.finalTerritoryPercent;
  if (policy.type === "survival") return standing.survivalTimeMs;
  if (policy.type === "placement_points") {
    return policy.points[Math.max(0, (standing.entrant.placement ?? 1) - 1)] ?? 0;
  }
  return standing.entrant.score ?? 0;
}

export function rankStandings(stage: TournamentStage, standings: readonly TournamentStanding[]): TournamentStanding[] {
  return standings.map((standing) => ({ ...standing, entrant: { ...standing.entrant } }))
    .sort((left, right) => compareStandings(left, right, stage.tieBreakers))
    .map((standing, index) => ({ ...standing, placement: index + 1 }));
}

export function advanceStage(
  stage: TournamentStage,
  standings: readonly TournamentStanding[],
  staffOverrideEntrantIds: readonly string[] = [],
): TournamentEntrant[] {
  const ranked = rankStandings(stage, standings);
  const withResult = (standing: TournamentStanding): TournamentEntrant => ({
    ...standing.entrant,
    score: standing.score,
    placement: standing.placement,
  });
  if (stage.advancement.type === "winner") return ranked.slice(0, 1).map(withResult);
  if (stage.advancement.type === "top_k" || stage.advancement.type === "points") {
    return ranked.slice(0, stage.advancement.count).map(withResult);
  }
  if (stage.advancement.type === "team_top_k") {
    const teamScores = new Map<string, number>();
    for (const standing of ranked) {
      if (standing.entrant.teamId) teamScores.set(standing.entrant.teamId, (teamScores.get(standing.entrant.teamId) ?? 0) + standing.score);
    }
    const teams = new Set([...teamScores.entries()]
      .sort((left, right) => right[1] - left[1] || left[0].localeCompare(right[0]))
      .slice(0, stage.advancement.count)
      .map(([teamId]) => teamId));
    return ranked.filter((standing) => standing.entrant.teamId && teams.has(standing.entrant.teamId)).map(withResult);
  }
  if (staffOverrideEntrantIds.length === 0) throw new Error("Staff override advancement requires entrant IDs");
  const byId = new Map(ranked.map((standing) => [standing.entrant.id, standing]));
  return staffOverrideEntrantIds.map((id) => {
    const standing = byId.get(id);
    if (!standing) throw new Error(`Staff override references unknown entrant: ${id}`);
    return withResult(standing);
  });
}

function compareStandings(
  left: TournamentStanding,
  right: TournamentStanding,
  tieBreakers: TournamentStage["tieBreakers"],
): number {
  if (right.score !== left.score) return right.score - left.score;
  for (const tieBreaker of tieBreakers) {
    const comparison = tieBreaker === "peak_territory"
      ? right.peakTerritoryPercent - left.peakTerritoryPercent
      : tieBreaker === "survival_time"
        ? right.survivalTimeMs - left.survivalTimeMs
        : tieBreaker === "eliminations"
          ? right.eliminations - left.eliminations
          : (left.entrant.seed ?? Number.MAX_SAFE_INTEGER) - (right.entrant.seed ?? Number.MAX_SAFE_INTEGER);
    if (comparison !== 0) return comparison;
  }
  return left.entrant.id.localeCompare(right.entrant.id);
}
